/**
 * threadNotifications — the per-thread notification mode and its defaults
 * (owner directive 2026-08-26).
 *
 * The chat header carries a bell that cycles THREE states, per user, per
 * thread:
 *   1. 'chime'          — a quick chime on each incoming message (the default);
 *   2. 'chime-platform' — the chime plus a platform (browser) notification per
 *                         incoming message, in real time;
 *   3. 'off'            — silent.
 *
 * The choice is the VIEWER's alone ("not everyone in the group thread") and is
 * per thread. The settings page carries DEFAULTS — thread and group-thread
 * rows for chime (default on) and platform/email/text (default off) — which
 * apply only when a thread is FIRST encountered: the resolved mode is stamped
 * per-thread at that moment, so changing the defaults later never alters a
 * thread the user already has ("their settings within threads already
 * established do not change").
 *
 * Email/text delivery is a server concern; the defaults persist alongside the
 * other notification channel preferences (users/{uid}/user_settings) for the
 * dispatch plane to consume. This module owns everything the BROWSER does:
 * mode storage/resolution, the cycle, the chime, and the platform
 * notification.
 *
 * Pure state + storage helpers, no React — unit-tested directly.
 */

export type ThreadNotifyMode = 'chime' | 'chime-platform' | 'off';

/** One defaults row: the chime plus the three server-delivered channels. */
export interface ThreadNotificationDefaultRow {
  chime: boolean;
  platform: boolean;
  email: boolean;
  text: boolean;
}

export interface ThreadNotificationDefaults {
  thread: ThreadNotificationDefaultRow;
  groupThread: ThreadNotificationDefaultRow;
}

/** The stated factory defaults: chime on, every other channel off, both kinds. */
export const FACTORY_THREAD_NOTIFICATION_DEFAULTS: ThreadNotificationDefaults = {
  thread: { chime: true, platform: false, email: false, text: false },
  groupThread: { chime: true, platform: false, email: false, text: false },
};

const DEFAULTS_STORAGE_KEY = 'threadNotificationDefaults';

/**
 * Per-USER defaults key (2026-08-27 review fix): the unscoped key leaked one
 * account's defaults into the next on a shared browser — and because the
 * per-thread stamp freezes whatever defaults it read, the leak was permanent
 * for every thread first encountered under the wrong identity. The unscoped
 * key remains the read fallback (and the write target when no uid is known),
 * so pre-fix stored defaults keep working.
 */
function defaultsStorageKey(uid?: string | null): string {
  const id = (uid ?? '').trim();
  return id ? `${DEFAULTS_STORAGE_KEY}:v1:${id}` : DEFAULTS_STORAGE_KEY;
}

function sanitizeRow(raw: unknown, fallback: ThreadNotificationDefaultRow): ThreadNotificationDefaultRow {
  if (!raw || typeof raw !== 'object') return { ...fallback };
  const rec = raw as Record<string, unknown>;
  return {
    chime: typeof rec.chime === 'boolean' ? rec.chime : fallback.chime,
    platform: typeof rec.platform === 'boolean' ? rec.platform : fallback.platform,
    email: typeof rec.email === 'boolean' ? rec.email : fallback.email,
    text: typeof rec.text === 'boolean' ? rec.text : fallback.text,
  };
}

export function loadThreadNotificationDefaults(uid?: string | null): ThreadNotificationDefaults {
  try {
    const raw = typeof localStorage !== 'undefined'
      ? (localStorage.getItem(defaultsStorageKey(uid))
          ?? localStorage.getItem(DEFAULTS_STORAGE_KEY))
      : null;
    if (!raw) return FACTORY_THREAD_NOTIFICATION_DEFAULTS;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    return {
      thread: sanitizeRow(parsed.thread, FACTORY_THREAD_NOTIFICATION_DEFAULTS.thread),
      groupThread: sanitizeRow(parsed.groupThread, FACTORY_THREAD_NOTIFICATION_DEFAULTS.groupThread),
    };
  } catch {
    return FACTORY_THREAD_NOTIFICATION_DEFAULTS;
  }
}

export function saveThreadNotificationDefaults(
  defaults: ThreadNotificationDefaults,
  uid?: string | null,
): void {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(defaultsStorageKey(uid), JSON.stringify(defaults));
    }
  } catch { /* preference degrades to factory defaults on this device */ }
}

/**
 * Map a defaults row onto the bell's three-state mode. Platform-on implies the
 * chime (that is what the cycle's second state means); chime alone is the
 * first state; neither is off.
 */
export function modeFromDefaults(defaults: ThreadNotificationDefaults, isGroup: boolean): ThreadNotifyMode {
  const row = isGroup ? defaults.groupThread : defaults.thread;
  if (row.platform) return 'chime-platform';
  if (row.chime) return 'chime';
  return 'off';
}

/** The bell's click order: chime → chime-platform → off → chime → … */
export function cycleThreadNotifyMode(mode: ThreadNotifyMode): ThreadNotifyMode {
  switch (mode) {
    case 'chime': return 'chime-platform';
    case 'chime-platform': return 'off';
    default: return 'chime';
  }
}

function isThreadNotifyMode(raw: unknown): raw is ThreadNotifyMode {
  return raw === 'chime' || raw === 'chime-platform' || raw === 'off';
}

/** Per-user, per-thread mode key. */
function modeStorageKey(uid: string, threadId: string): string {
  return `prest0n:thread_notify:v1:${uid}:${threadId}`;
}

export function getStoredThreadNotifyMode(uid: string, threadId: string): ThreadNotifyMode | null {
  if (!uid || !threadId) return null;
  try {
    const raw = typeof localStorage !== 'undefined'
      ? localStorage.getItem(modeStorageKey(uid, threadId))
      : null;
    return isThreadNotifyMode(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function setStoredThreadNotifyMode(uid: string, threadId: string, mode: ThreadNotifyMode): void {
  if (!uid || !threadId) return;
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(modeStorageKey(uid, threadId), mode);
    }
  } catch { /* the in-memory state still holds for this session */ }
}

/**
 * Resolve a thread's effective mode, establishing it on FIRST ENCOUNTER.
 *
 * Order: the per-thread stored choice (never rewritten by a defaults change) →
 * the CURRENT defaults, which are then STAMPED for this thread so it counts as
 * established from now on — that stamp is precisely what keeps later defaults
 * edits away from threads the user already has.
 */
export function resolveThreadNotifyMode(args: {
  uid: string;
  threadId: string;
  isGroup: boolean;
  defaults?: ThreadNotificationDefaults;
}): ThreadNotifyMode {
  const { uid, threadId, isGroup } = args;
  if (!uid || !threadId) return 'chime';
  const stored = getStoredThreadNotifyMode(uid, threadId);
  if (stored) return stored;
  const mode = modeFromDefaults(args.defaults ?? loadThreadNotificationDefaults(uid), isGroup);
  setStoredThreadNotifyMode(uid, threadId, mode);
  return mode;
}

// ─── The chime ───────────────────────────────────────────────────────────────

let audioContext: AudioContext | null = null;
let lastChimeAt = 0;
/** Never chime more often than this — a burst of arrivals is ONE event. */
export const CHIME_MIN_INTERVAL_MS = 1_500;

/**
 * A quick two-tone chime, synthesized (no asset to load or cache-bust).
 * Quiet by design; throttled so a flurry of messages reads as one arrival.
 */
export function playThreadChime(now: number = Date.now()): boolean {
  if (now - lastChimeAt < CHIME_MIN_INTERVAL_MS) return false;
  try {
    const Ctor = (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }).AudioContext
      || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return false;
    if (!audioContext) audioContext = new Ctor();
    const ctx = audioContext;
    if (ctx.state === 'suspended') void ctx.resume().catch(() => undefined);
    const t0 = ctx.currentTime;
    for (const [freq, start, dur] of [[880, 0, 0.09], [1174.66, 0.09, 0.14]] as const) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, t0 + start);
      gain.gain.exponentialRampToValueAtTime(0.06, t0 + start + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + start + dur);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t0 + start);
      osc.stop(t0 + start + dur + 0.02);
    }
    lastChimeAt = now;
    return true;
  } catch {
    return false;
  }
}

// ─── Platform notifications ──────────────────────────────────────────────────

/**
 * Ask for platform-notification permission — called when the user CLICKS into
 * the 'chime-platform' state (a user gesture, which is when browsers allow
 * the prompt). Best-effort; 'denied' simply means the state behaves like
 * 'chime' until the user changes their browser setting.
 */
export function ensurePlatformNotificationPermission(): void {
  try {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission === 'default') {
      void Notification.requestPermission().catch(() => undefined);
    }
  } catch { /* unsupported — chime still works */ }
}

/** Show one platform notification for an incoming thread message. */
export function showThreadPlatformNotification(args: {
  /** Tag key: one live notification PER THREAD (2026-08-27 review fix — a
   * title-keyed tag made same-titled and untitled threads replace each
   * other's notifications). */
  threadId?: string;
  threadTitle: string;
  senderLabel?: string;
  preview?: string;
}): boolean {
  try {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return false;
    const title = (args.threadTitle || '').trim() || 'new message';
    const who = (args.senderLabel || '').trim();
    const preview = (args.preview || '').trim().slice(0, 140);
    const body = who && preview ? `${who}: ${preview}` : (preview || who || 'new message');
    const tagKey = (args.threadId || '').trim() || title;
    // tag: one live notification per thread — a burst REPLACES rather than stacks.
    // eslint-disable-next-line no-new
    new Notification(title, { body, tag: `prest0n-thread-${tagKey}`, silent: true });
    return true;
  } catch {
    return false;
  }
}
